<template>
	<div
		class="slider"
		:key="current.url"
		:class="['slider--slide-count-' + slideCounter, 'slider--' + settings.sliderHeight]"
	>
		<div>
			<transition :css="showTransition" name="slider" mode="in-out">
				<component
					:is="tag"
					:key="current.src"
					:value="current.src"
					:thumbnail="current.thumbnail"
					:focalPoint="current.focalPoint"
					ref="content"
					class="slider__element"
					:style="{ '--slider-transition-duration': slideDuration + 'ms' }"
					@timeupdate="onTimeUpdate"
					:class="{
						'slider__element--fullscreen': current.fullscreen,
						'slider__element--fixed-heading': settings.sliderHeadingMaster,
					}"
				>
					<template #overlay>
						<div class="slider__overlay">
							<h1 :class="['hero--' + (settings.fontSize || 'small')]" :style="overlayStyle">{{ heading }}</h1>

							<ButtonComponent
								v-if="current.button"
								tag="a"
								:label="current.button.buttonText"
								:href="current.button.path"
							/>
						</div>
					</template>
				</component>
			</transition>
		</div>

		<template v-if="settings.scrollIndicator">
			<div class="slider__scroll-indicator" @click="scrollToPageContent">
				<Lottie :options="scrollIndricatorOptions" :height="40" :width="40" v-on:animCreated="setLottieSpeed" />
			</div>

			<div class="slider__scroll-info" @click="scrollToPageContent">
				Scroll for å lese mer
			</div>
		</template>
	</div>
</template>

<script>
import ImageComponent from './Types/Image'
import VimeoComponent from './Types/Vimeo'
import Lottie from 'vue-lottie'
import scrollIndicatorData from '../../assets/scroll-down.json'

export default {
	props: {
		value: {
			type: Array,
			default: () => [],
		},
		settings: {
			type: Object,
			default: () => ({}),
		},
		duration: {
			type: Number,
			default: 4000,
		},
	},
	watch: {
		$route(to, from) {
			this.showTransition = false
		},
	},
	data() {
		return {
			anim: null,
			slideCounter: 0,
			slideDuration: 800,
			interval: null,
			showFooter: false,
			isScrolled: true,
			showTransition: false,
			scrollIndricatorOptions: { animationData: scrollIndicatorData },
		}
	},
	computed: {
		currentIndex() {
			return this.slideCounter % this.value.length
		},
		heading() {
			return this.settings.sliderHeadingMaster || this.$path(this.current, 'overlayContent.heading')
		},
		overlayStyle() {
			let uppercase = this.$path(this.current, 'overlayContent.uppercase')
			return ` text-transform: ${uppercase ? 'uppercase' : 'none'};`
		},
		tag() {
			switch (this.current.type) {
				case 'video':
					return VideoComponent
				case 'image':
					return ImageComponent
				case 'vimeo':
					return VimeoComponent
			}
		},
		current() {
			return this.value[this.currentIndex] || {}
		},
	},
	methods: {
		start() {
			this.stop()
			if (['video', 'vimeo'].includes(this.current.type)) return
			this.interval = setTimeout(() => this.next(), this.duration)
		},
		stop() {
			clearInterval(this.interval)
			this.interval = null
		},
		onEnter() {
			this.stop()
		},
		onLeave() {
			this.start()
		},
		next() {
			this.slideCounter++

			this.start()
		},
		prev() {
			this.slideCounter--
			this.start()
		},
		scrollToPageContent() {
			let element = document.querySelector('.page-module__content')
			if (!element) return
			const y = element.getBoundingClientRect().top + window.pageYOffset - 80

			window.scrollTo({
				top: y,
				behavior: 'smooth',
			})
		},
		onTimeUpdate({ total, elapsed }) {
			if (total - elapsed <= this.slideDuration) this.next()
		},
		setLottieSpeed(anim) {
			this.anim = anim
			this.anim.setSpeed(0.6)
		},
	},
	mounted() {
		this.start()
		this.showTransition = true
	},
	components: {
		Lottie,
	},
}
</script>

<style lang="scss">
@import '@/styles/base/_variables.scss';
@import '@/styles/utils/_mixins.scss';

.slider {
	$easing: cubic-bezier(0.7, 0, 0.3, 1);

	position: relative;
	width: 100%;
	overflow: hidden;
	height: var(--height);
	display: flex;
	flex-direction: column;

	&--small {
		--height: 400px;
		@include respond-below('phone') {
			--height: 300px;
		}
	}

	&--medium {
		--height: 500px;
		@include respond-below('phone') {
			--height: 400px;
		}
	}

	&--large {
		--height: 700px;
		@include respond-below('phone') {
			--height: 500px;
		}
	}

	&__overlay {
		color: white;
		text-align: center;
		z-index: 2;
		width: 100%;
		max-width: 680px;
		margin: 0;
		@include respond-below('phone') {
			max-width: 300px;
		}

		&-wrapper {
			width: 100vw;
			height: var(--height);
			position: absolute;
			top: 0;
			left: 0;
			z-index: 1;

			display: flex;
			justify-content: center;
			align-items: center;

			@keyframes slideIn {
				from {
					transform: translateX(30%);
					opacity: 0;
				}
			}

			.slider__element:not(.slider__element--fixed-heading) & {
				animation: slideIn var(--slider-transition-duration) $easing forwards;
				animation-delay: var(--slider-transition-duration);
			}

			h1 {
				@include respond-below('phone') {
					position: relative;
					top: 1.5rem;
				}
			}
		}
	}

	&.slider--slide-count-0 .slider__overlay-wrapper {
		@keyframes slideUp {
			from {
				transform: translateY(5%);
				opacity: 0;
			}
		}
		animation-name: slideUp;
		animation-duration: var(--slider-transition-duration);
		animation-delay: 0ms;
	}

	&.slider--slide-count-1 .slider__element:nth-last-child(2) .slider__overlay-wrapper {
		animation-name: none;
		animation-delay: 0ms;
	}

	&__element {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;

		overflow: hidden;
	}
	&__action {
		z-index: 3;
		position: absolute;
		right: 50%;
		bottom: calc(var(--height) - 50%);
		transform: translate(50%, 50%);
	}

	&__scroll {
		&-indicator {
			z-index: 2;
			position: absolute;
			right: 50%;

			bottom: calc(var(--height) - 100% + 90px);
			transform: translate(50%, 50%);
		}
		&-info {
			position: absolute;
			z-index: 2;

			right: 50%;
			bottom: calc(var(--height) - 100% + 35px);

			transform: translate(50%, 50%);
		}
	}

	.slider-enter-active {
		transition-duration: var(--slider-transition-duration);
		z-index: -1;
	}

	.slider-enter-active,
	.slider-leave-active {
		transition-property: width;
		transition-timing-function: $easing;
	}

	.slider-leave-active {
		transition-duration: var(--slider-transition-duration);
		z-index: 2;

		.slider__overlay-wrapper {
			@keyframes slideOut {
				to {
					transform: translateX(-30%);
					opacity: 0;
				}
			}
			animation-name: slideOut !important;
			animation-delay: 0ms;
		}
	}

	.slider-leave-to {
		width: 0;
	}
}
</style>
